import { Config } from '../config';

/**
 * Live survey stream.
 *
 * RF observation is continuous: signal, client counts and even which access
 * points are visible change second to second, and the adapter hops channels so
 * any single reading is partial. The socket therefore carries a rolling view
 * rather than a result.
 *
 * Reconnect is not optional. The API runs under gunicorn with max_requests
 * recycling, so a worker will eventually close this socket mid-survey through
 * no fault of either end — that has to look like a brief gap, not a dead page.
 */
class WirelessSurveySocket {
  constructor() {
    this.ws = null;
    this.engagementId = null;
    this.handlers = { survey: [], status: [], error: [] };
    this.reconnectTimer = null;
    this.reconnectDelay = 3000;
    this.manualClose = false;
  }

  connect(engagementId) {
    this.disconnect();
    this.manualClose = false;
    this.engagementId = engagementId;

    const token = localStorage.getItem('token');
    if (!token) {
      this._emit('error', { error: 'Not authenticated' });
      return;
    }

    const url = `${Config.WSBaseURL}/wireless/ws/${engagementId}/${token}`;
    try {
      this.ws = new WebSocket(url);
    } catch (err) {
      this._emit('error', { error: err.message });
      this._scheduleReconnect();
      return;
    }

    this.ws.onopen = () => this._emit('status', { state: 'connected' });

    this.ws.onmessage = (event) => {
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }
      if (payload.type === 'survey') {
        this._emit('survey', payload);
        this._emit('status', { state: 'streaming' });
      } else if (payload.type === 'degraded') {
        // One dropped frame is unremarkable over a VPN to a laptop in a
        // customer's office. Surface it without tearing the view down.
        this._emit('status', { state: 'degraded', detail: payload.error });
      } else if (payload.type === 'connected') {
        this._emit('status', { state: 'connected', agent: payload.agent });
      } else if (payload.type === 'error') {
        this._emit('error', payload);
      }
    };

    this.ws.onerror = () => this._emit('status', { state: 'degraded' });

    this.ws.onclose = () => {
      this._emit('status', { state: 'disconnected' });
      if (!this.manualClose) this._scheduleReconnect();
    };
  }

  _scheduleReconnect() {
    if (this.reconnectTimer || this.manualClose) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.engagementId && !this.manualClose) this.connect(this.engagementId);
    }, this.reconnectDelay);
  }

  disconnect() {
    this.manualClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
  }

  on(event, handler) {
    if (!this.handlers[event]) this.handlers[event] = [];
    this.handlers[event].push(handler);
    return () => {
      this.handlers[event] = this.handlers[event].filter((h) => h !== handler);
    };
  }

  _emit(event, payload) {
    (this.handlers[event] || []).forEach((handler) => {
      try {
        handler(payload);
      } catch (err) {
        console.error(`wireless socket handler failed for ${event}:`, err);
      }
    });
  }
}

const wirelessSurveySocket = new WirelessSurveySocket();
export default wirelessSurveySocket;
